import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { LeagueWeek } from '../../../models/LeagueWeek';
import { Player } from '../../../models/Player';
import { GolfEventService } from '../../../services/golf-event.service';
import { NgFor, NgIf } from '@angular/common';
import { GolfEventField } from '../../../models/golfeventfield';
import { PostIndividualRoundComponent } from './post-individual-round/post-individual-round.component';
import { PostTeamRoundComponent } from './post-team-round/post-team-round.component';
import { PostScrambleRoundComponent } from './post-scramble-round/post-scramble-round.component';
import { LeagueRound } from '../../../models/LeagueRound';
import { GolfEvent } from '../../../models/GolfEvent';
import { WeeklyLeaderboard } from '../../../models/weeklyleaderboard';
import { ApplicationService } from '../../../services/application.service';
import { PERMISSIONS, EVENT_FORMAT } from '../../../models/constants';
import { WeekDetailsComponent } from '../week-details/week-details.component';
import { LeagueRoundRowViewModel } from '../../../models/viewmodels/leagueRoundRowViewModel';
import { AuthenticationService } from '../../../services/authentication.service';
import { isEventOver } from '../../../util/dateutil';

@Component({
  selector: 'app-weekly-round-list',
  standalone: true,
  imports: [NgFor, NgIf, PostIndividualRoundComponent, PostTeamRoundComponent, PostScrambleRoundComponent, WeekDetailsComponent],
  templateUrl: './weekly-round-list.component.html',
  styleUrl: './weekly-round-list.component.css'
})
export class WeeklyRoundListComponent implements OnChanges {
  @Input() league:GolfEvent|null = null;
  @Input() leagueWeek:LeagueWeek|null = null;
  @Input() loggedInPlayerId:number|null = null;
  @Input() loggedInPlayerHasPaid:boolean = false;
  @Input() paidPlayers:Array<GolfEventField> = [];
  @Input() openPostTrigger:number = 0;
  @Input() hidePostButton:boolean = false;

  constructor(private golfEventService:GolfEventService, private applicationService:ApplicationService, private authenticationService:AuthenticationService) {}

  players:Array<Player> = [];
  leaderboard:Array<WeeklyLeaderboard> = [];
  containerDisplay:string = 'ROUNDLIST'
  roundsToCount:number = 1;
  roundListTypeDisplay:string = 'ALLROUNDS';
  allRounds:Array<LeagueRoundRowViewModel> = [];
  usingHandicaps:boolean = false;
  isAdmin:boolean = false;

  @Output() editWeek = new EventEmitter<LeagueWeek>();
  @Output() postingChange = new EventEmitter<boolean>();

  get isSingleCourse():boolean {
    return this.league?.type === EVENT_FORMAT.SINGLE_COURSE;
  }

  get isHybridScramble():boolean {
    return this.league?.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get isEventOver():boolean {
    return isEventOver(this.league?.endDate);
  }

  get canEditWeek():boolean {
    return this.isAdmin && !this.isEventOver;
  }

  get canPostRound():boolean {
    return this.loggedInPlayerHasPaid && !this.hidePostButton && !this.isEventOver;
  }

  // Hybrid scramble rounds are posted by a pair but scored individually, so the round
  // reads as "Joe & Alex".
  roundLabel(round:LeagueRound):string {
    if (this.league?.isTeamEvent && !this.isHybridScramble) {
      return round.teamName ?? '';
    }

    const player = `${round.individualFirstName} ${round.individualLastName}`;

    if (round.guestPartnerName) {
      const partner = `${round.guestPartnerName} (Guest)`;
      return [player, partner].sort((a, b) => a.localeCompare(b)).join(' & ');
    }

    if (round.partnerFirstName) {
      const partner = [round.partnerFirstName, round.partnerLastName].filter(Boolean).join(' ');
      return [player, partner].sort((a, b) => a.localeCompare(b)).join(' & ');
    }

    return player;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if(this.authenticationService.loginResult)
      this.isAdmin = await this.authenticationService.hasPermission(PERMISSIONS.EVENTADMIN);

    if (changes['leagueWeek'] && changes['leagueWeek'].currentValue) {
      this.usingHandicaps = this.league ? this.league.useHandicaps : false;
      this.containerDisplay = 'ROUNDLIST';
      this.roundListTypeDisplay = this.isSingleCourse ? 'ALLROUNDS' : 'LEADERBOARD';
      this.getRounds();
      if (!this.isSingleCourse) {
        this.getLeaderboard();
      }
      this.postingChange.emit(false);
    }

    // Only open when the parent bumps the trigger while this view is already open.
    // Ignore the first change so remounting a course after cancel does not reopen the form.
    if (changes['openPostTrigger']
        && !changes['openPostTrigger'].firstChange
        && this.openPostTrigger > 0
        && !this.isEventOver
        && changes['openPostTrigger'].previousValue !== this.openPostTrigger) {
      setTimeout(() => this.postRound(), 0);
    }
  }

  requestEditWeek():void {
    if (!this.leagueWeek || this.isEventOver) return;
    this.editWeek.emit(this.leagueWeek);
  }

  async getRounds(){
    if(!this.leagueWeek) return;
    this.applicationService.showGlobalSpinner();
    let rounds = await this.golfEventService.getLeagueRounds(this.leagueWeek.id);
    this.allRounds = this.collapseScramblePairs(rounds).map<LeagueRoundRowViewModel>(r => ({
                        round: r,
                        isConfirming: false
                      }));
    this.applicationService.hideGlobalSpinner();
  }

  // Scramble posts create two DB rows (one credit per player). Show one history row per post.
  collapseScramblePairs(rounds:Array<LeagueRound>):Array<LeagueRound> {
    const seenGroups = new Set<string>();
    const collapsed:Array<LeagueRound> = [];

    for (const round of rounds) {
      if (!round.scrambleGroupId) {
        collapsed.push(round);
        continue;
      }

      if (seenGroups.has(round.scrambleGroupId)) {
        continue;
      }

      seenGroups.add(round.scrambleGroupId);

      const sibling = rounds.find(
        r => r.scrambleGroupId === round.scrambleGroupId && r.id !== round.id
      );

      collapsed.push({
        ...round,
        // Either partner counting this net on the leaderboard is enough to mark the pair row.
        scoreUsedOnLeaderboard: round.scoreUsedOnLeaderboard || !!sibling?.scoreUsedOnLeaderboard
      });
    }

    return collapsed;
  }

  async getLeaderboard(){
    if(!this.leagueWeek) return;
    this.applicationService.showGlobalSpinner();
    this.leaderboard = await this.golfEventService.getWeeklyLeaderboard(this.leagueWeek.id);
    if(this.leaderboard.length > 0) {
      this.roundsToCount = this.leaderboard[0].roundsToCount;
    }
    this.applicationService.hideGlobalSpinner();
  }

  postRound():void {
    if(!this.league || !this.loggedInPlayerHasPaid || this.isEventOver) return;
    let roundWindow = 'POSTINDIVIDUALROUND';
    if (this.isHybridScramble) {
      roundWindow = 'POSTSCRAMBLEROUND';
    } else if (this.league.isTeamEvent) {
      roundWindow = 'POSTTEAMROUND';
    }
    this.containerDisplay = roundWindow;
    this.postingChange.emit(true);
  }

  async deleteRound(round:LeagueRoundRowViewModel){
    this.applicationService.showGlobalSpinner();
    await this.golfEventService.deleteLeagueRound(round.round.id);
    this.applicationService.hideGlobalSpinner();
    await this.getRounds();
  }

  scoresPosted(): void {
    this.containerDisplay = 'ROUNDLIST'
    this.postingChange.emit(false);
    this.applicationService.showGlobalSpinner();
    setTimeout(() => {
      this.getRounds();
      this.getLeaderboard(); //Give the leaderboard a couple of seconds to rebuild.
    }, 2000);
  }

  backToRoundList():void {
    this.containerDisplay = 'ROUNDLIST';
    this.postingChange.emit(false);
  }

  formatLocalDateTime(utcString: string): string {
    return this.applicationService.formatLocalDateTime(utcString);
  }

  formatScore(score:number):string {
    if(!this.league)
      return score.toString();

    if(this.league.scoringType === 'SCORETOPAR')
      return this.applicationService.formatScoreToPar(score);
    else
      return score.toString();
  }

  isCourseOfficial(entry:WeeklyLeaderboard):boolean {
    return entry.roundsCounted >= (this.roundsToCount || entry.roundsToCount || 1);
  }

  courseRank(index:number):string {
    if (!this.isHybridScramble) return (index + 1).toString();
    if (!this.isCourseOfficial(this.leaderboard[index])) return '';

    let rank = 0;
    for (let i = 0; i <= index; i++) {
      if (this.isCourseOfficial(this.leaderboard[i])) rank++;
    }
    return rank.toString();
  }
}

